import type { OverrideFormModel, OverrideInputEntry, OverrideProperty, StoredOverrideEntry } from "./types";

const recordToPairs = (r: Record<string, string>): OverrideProperty[] =>
  Object.entries(r).map(([key, template]) => ({ key, template }));

const pairsToRecord = (pairs: OverrideProperty[]): Record<string, string> =>
  Object.fromEntries(pairs.filter((p) => p.key.trim() !== "").map((p) => [p.key, p.template]));

const isScoped = (e: StoredOverrideEntry): boolean => e.composers !== undefined;

/**
 * Parse stored overrides into the editor model. At most one unscoped (all-targets)
 * entry is editable; scoped entries are preserved read-only; more than one unscoped
 * entry blocks editing entirely so a save can never collapse them into one.
 */
export const storedToForm = (overrides: StoredOverrideEntry[] | undefined): OverrideFormModel => {
  const all = overrides ?? [];
  const scoped = all.filter(isScoped);
  const unscoped = all.filter((e) => !isScoped(e));

  if (unscoped.length > 1) {
    return { editable: false, entity: [], relation: [], preservedScoped: all, blockedReason: "multipleUnscoped" };
  }

  const one = unscoped[0];
  return {
    editable: true,
    entity: recordToPairs(one?.override.entity?.properties ?? {}),
    relation: recordToPairs(one?.override.relation?.properties ?? {}),
    preservedScoped: scoped,
    blockedReason: null,
  };
};

const storedEntryToInput = (e: StoredOverrideEntry): OverrideInputEntry => ({
  composers: e.composers,
  entityProperties: e.override.entity ? recordToPairs(e.override.entity.properties) : undefined,
  relationProperties: e.override.relation ? recordToPairs(e.override.relation.properties) : undefined,
});

/**
 * Build the full mutation `overrides` array: preserved scoped entries (re-serialized
 * through the same conversion, never a raw passthrough — stored and input shapes
 * differ) plus the single edited all-targets entry. The all-targets entry is omitted
 * when it carries no non-empty rows, so an empty form clears all overrides.
 */
export const formToInput = (form: OverrideFormModel): OverrideInputEntry[] => {
  const preserved = form.preservedScoped.map(storedEntryToInput);
  const entity = pairsToRecord(form.entity);
  const relation = pairsToRecord(form.relation);
  const hasEntity = Object.keys(entity).length > 0;
  const hasRelation = Object.keys(relation).length > 0;
  if (!hasEntity && !hasRelation) return preserved;
  const allTargets: OverrideInputEntry = {
    entityProperties: hasEntity ? recordToPairs(entity) : undefined,
    relationProperties: hasRelation ? recordToPairs(relation) : undefined,
  };
  return [...preserved, allTargets];
};
